import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DashboardPage: React.FC = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.firstName}!
        </Typography>
        
        <Typography variant="h6" color="textSecondary" gutterBottom>
          User Type: {user?.userType?.toUpperCase()}
          {user?.userType === 'admin' && (
            <>
              {' '}({isSuperAdmin() ? 'Super Administrator' : 'Administrator'})
            </>
          )}
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {user?.userType === 'student' && (
            <>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Available Assessments
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Take psychological assessments assigned by your counselor
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/assessments')}>
                      View Assessments
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      My Results
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      View your completed assessment results
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/results')}>
                      View Results
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Appointments
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Schedule and manage appointments with counselors
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/appointments')}>
                      Manage Appointments
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {user?.userType === 'provider' && (
            <>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Student Management
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Manage your assigned students and their assessments
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/students')}>
                      View Students
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Assessment Results
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Review student assessment results and risk flags
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/provider/results')}>
                      View Results
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Pending Patients
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Review and approve patient registration requests
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/pending-patients')}>
                      View Pending
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Manage Surveys
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Create, edit, and manage psychological assessments
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/manage-surveys')}>
                      Manage Surveys
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      View Assigned Surveys
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Track assigned surveys and their completion status
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/assigned-surveys')}>
                      View Assigned
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      My Groups
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Manage teams and group memberships
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/teams')}>
                      View Groups
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      View Organizations
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      View departments and organizations within your university
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/organizations')}>
                      View Organizations
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Follow-ups List
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Manage patient follow-up queue and track communications
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/followups')}>
                      View Follow-ups
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {user?.userType === 'admin' && (
            <>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Overview
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      View system-wide statistics and reports
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/reports')}>
                      View Reports
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      User Management
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Manage students, providers, and administrators
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/user-management')}>
                      Manage Users
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      University Settings
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Configure university and department settings
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/settings')}>
                      Manage Settings
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              {isSuperAdmin() && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        University Management
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Add, edit, and manage universities in the system
                      </Typography>
                      <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/universities')}>
                        Manage Universities
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Teams & Groups
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Manage teams, departments, and group memberships
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/teams')}>
                      Manage Teams
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Common section for all users */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  My Profile
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage your personal information and account settings
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/profile')}>
                  Edit Profile
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            System Status
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ✅ Backend API: Connected<br/>
            ✅ Database: PostgreSQL Connected<br/>
            ✅ Authentication: JWT Active<br/>
            📊 This is a demo system with sample data
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default DashboardPage;